/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  ACCOUNT_SETTINGS_ROUTE_PERMISSION,
  PAYMENTS_ROUTE_PERMISSION,
  STATEMENTS_ROUTE_PERMISSION,
} from "@farmlands/account-management";
import { CARDS_ROUTE_PERMISSION } from "@farmlands/card-management";
import { getFeatureRoutePermissionKeys } from "@farmlands/permissions";
import {
  Calendar,
  CircleDollarSign,
  CreditCard,
  FileText,
  Folder,
  LucideIcon,
  Receipt,
  ScrollText,
  Settings,
  Sprout,
} from "lucide-react";
import { ReactNode, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useAnyPermission } from "../../../../hooks/useAnyPermission";
import { usePermission } from "../../../../hooks/usePermission";
import {
  selectRemoteConfig,
  selectShouldShowActivities,
} from "../../../../services/firebase/remote-config/remoteConfig.slice";
import { useAppDispatch } from "../../../../store/rootStore";
import TestIds from "../../../../testIds";
import { ScrollYPositionKeys } from "../../../../util/consts";
import { ACTIVITIES_ROUTES } from "../../../activities/constants";
import {
  DigitalFarmerAccount,
  selectCurrentFarmerAccount,
  selectIsThisTfo,
} from "../../../azure-b2c/storage";
import { CARDS_ROUTES } from "../../../cards/constants";
import DeclarationModal from "../../../declarations/components/DeclarationsModal";
import { getDeclarations } from "../../../declarations/declarations.slice";
import { ORDERS_ROUTES } from "../../../orders/constants";
import { QUOTES_ROUTES } from "../../../quotes/constants";
import { SEED_ROUTES } from "../../../seed-orders/constants";
import { ACCOUNT_SETTINGS_ROUTES } from "../../account-settings";
import { useSelectedAccountQuery } from "../../hooks/useSelectedAccountQuery";
import { PAYMENTS_ROUTES } from "../../payments/constants";
import { STATEMENTS_ROUTES } from "../../statements";

import { IFeatureLinksProps } from "./FeatureLinks";

export type FeatureLinkItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  testId: string;
  scrollYKey: ScrollYPositionKeys;
  onClick: () => void;
  children?: ReactNode;
  /** Items sharing a group render together; a divider is drawn between groups. */
  group: number;
};

export const useFeatureLinkItems = ({
  enableAccountDetails,
  enableQuotes,
  enableSeed,
  enableDeclarations,
  enableActivities,
  enableOrders,
  enableCards,
  enablePayments,
  enableStatements,
}: IFeatureLinksProps): FeatureLinkItem[] => {
  const selectedAccount: DigitalFarmerAccount | undefined = useSelector(selectCurrentFarmerAccount);
  const remoteConfig = useSelector(selectRemoteConfig);
  const canViewActivities = usePermission("StaffSpecific_Activities_ActivitiesView").allowed;
  const shouldShowActivities = useSelector(selectShouldShowActivities) && canViewActivities;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isTfo = useSelector(selectIsThisTfo);
  const canViewSalesAgreementsDeclarations = usePermission(
    "SalesAgreements_DeclarationsView"
  ).allowed;
  const canViewOrders = usePermission("Orders_OrderView").allowed;
  const canViewQuotes = usePermission("Quotes_QuoteView").allowed;
  const canCreateSeedOrder = usePermission("Orders_SeedOrderCreate").allowed;
  const canViewAccountSettings = useAnyPermission(
    getFeatureRoutePermissionKeys(ACCOUNT_SETTINGS_ROUTE_PERMISSION)
  ).allowed;
  const canManageCards = useAnyPermission(
    getFeatureRoutePermissionKeys(CARDS_ROUTE_PERMISSION)
  ).allowed;
  const canViewStatements = useAnyPermission(
    getFeatureRoutePermissionKeys(STATEMENTS_ROUTE_PERMISSION)
  ).allowed;
  const canViewPayments = useAnyPermission(
    getFeatureRoutePermissionKeys(PAYMENTS_ROUTE_PERMISSION)
  ).allowed;
  const { selectedAccount: myFarmlandsSelectedAccount } = useSelectedAccountQuery();

  const [openDeclarationsModal, setOpenDeclarationsModal] = useState(false);

  const hasSelectedAccount = !!selectedAccount;

  const items: (FeatureLinkItem & { visible: boolean })[] = [
    {
      id: "seed",
      label: "Seed",
      onClick: () => navigate(SEED_ROUTES.BASE),
      icon: Sprout,
      visible: hasSelectedAccount && remoteConfig.seedEnabled && !!enableSeed && canCreateSeedOrder,
      testId: TestIds.components.sideDrawer.seed,
      scrollYKey: ScrollYPositionKeys.Seeds,
      group: 1,
    },
    {
      id: "quotes",
      label: "Quotes",
      icon: Receipt,
      testId: TestIds.feat.account.userAccount.featureLinks.quotes,
      scrollYKey: ScrollYPositionKeys.Quotes,
      visible: !!(hasSelectedAccount && remoteConfig.quoteEnabled && enableQuotes && canViewQuotes),
      onClick: () => navigate(QUOTES_ROUTES.BASE),
      group: 1,
    },
    {
      id: "orders",
      label: "Orders",
      icon: ScrollText,
      testId: TestIds.feat.account.userAccount.featureLinks.orders,
      scrollYKey: ScrollYPositionKeys.Orders,
      visible: hasSelectedAccount && !!enableOrders && canViewOrders,
      onClick: () => navigate(ORDERS_ROUTES.BASE),
      group: 1,
    },
    {
      id: "declarations",
      label: "Declarations",
      icon: Folder,
      testId: TestIds.feat.account.userAccount.featureLinks.declarations,
      scrollYKey: ScrollYPositionKeys.Orders,
      visible: hasSelectedAccount && !!enableDeclarations && canViewSalesAgreementsDeclarations,
      onClick: () => {
        setOpenDeclarationsModal(true);
        dispatch(getDeclarations());
      },
      children: (
        <DeclarationModal
          open={openDeclarationsModal}
          handleClose={() => setOpenDeclarationsModal(false)}
        />
      ),
      group: 1,
    },
    {
      id: "statements",
      label: STATEMENTS_ROUTES.NAME,
      icon: FileText,
      testId: TestIds.feat.account.userAccount.featureLinks.statements,
      scrollYKey: ScrollYPositionKeys.Statements,
      visible: !!myFarmlandsSelectedAccount && !!enableStatements && !!canViewStatements,
      onClick: () => navigate(STATEMENTS_ROUTES.BASE),
      group: 2,
    },
    {
      id: "cards",
      label: CARDS_ROUTES.NAME,
      icon: CreditCard,
      testId: TestIds.feat.account.userAccount.featureLinks.cards,
      scrollYKey: ScrollYPositionKeys.Cards,
      visible: !!myFarmlandsSelectedAccount && !!enableCards && canManageCards,
      onClick: () => navigate(CARDS_ROUTES.BASE),
      group: 2,
    },
    {
      id: "payments",
      label: PAYMENTS_ROUTES.NAME,
      icon: CircleDollarSign,
      testId: TestIds.feat.account.userAccount.featureLinks.payments,
      scrollYKey: ScrollYPositionKeys.Payments,
      visible: !!myFarmlandsSelectedAccount && !!enablePayments && canViewPayments,
      onClick: () => navigate(PAYMENTS_ROUTES.BASE),
      group: 2,
    },
    {
      id: "account-details",
      label: ACCOUNT_SETTINGS_ROUTES.NAME,
      icon: Settings,
      testId: TestIds.feat.account.userAccount.featureLinks.accountDetails,
      scrollYKey: ScrollYPositionKeys.AccountSettings,
      visible: !!myFarmlandsSelectedAccount && !!enableAccountDetails && !!canViewAccountSettings,
      onClick: () => navigate(ACCOUNT_SETTINGS_ROUTES.BASE),
      group: 3,
    },
    {
      id: "activities",
      label: "Activities",
      icon: Calendar,
      testId: TestIds.feat.account.userAccount.featureLinks.activities,
      scrollYKey: ScrollYPositionKeys.Activities,
      visible: !!(hasSelectedAccount && shouldShowActivities && enableActivities && isTfo),
      onClick: () => navigate(ACTIVITIES_ROUTES.BASE),
      group: 3,
    },
  ];

  return items.filter((item) => item.visible);
};
